import type { ApolloQueryResult } from '@apollo/client';
import type { ReactNode } from 'react';

import { useApplicationActions } from '@pesto/hooks/src/useApplicationActions';
import { LoadingSpinner } from '@pesto/ui';
import { DropdownInput } from '@pesto/ui/components/DropdownInput';
import { Label } from '@pesto/ui/components/Label';
import { TextArea } from '@pesto/ui/components/TextArea';
import { humanize } from '@pesto/utils';
import { useCallback, useMemo, useState } from 'react';

import type { GetCreditApplicationByIdQuery } from '../../../__generated__/graphql/api';

import {
  AdminActionRecordType,
  CancellationType,
  DocCheckStatus,
  useAddDocCheckMutation,
  useApproveManualReviewMutation,
  useCloseApplicationMutation,
  useCreateActionRecordForCurrentAdminMutation,
  useDenyPartnerKycMutation,
  useRetryPartnerKycMutation,
  useReviewDocCheckMutation,
  useRunHardCreditCheckMaybeCardOrderMutation,
  useRunSoftCreditCheckMutation,
  useSendOfferMutation,
  useToggleDocVerificationMutation,
} from '../../../__generated__/graphql/api';
import { CloseApplication } from '../components/CloseApplication';

interface UseApplicationModalProps {
  application?: GetCreditApplicationByIdQuery['creditApplication'] | null;
}

export enum ApplicationActions {
  APPROVE = 'APPROVE',
  DENY_PARTNER_KYC = 'DENY_PARTNER_KYC',
  RETRY_PARTNER_KYC = 'RETRY_PARTNER_KYC',
  SEND = 'SEND',

  CLOSE_APPLICATION = 'CLOSE_APPLICATION',

  CREATE_LOG = 'CREATE_LOG',
  CREATING_LABELS = 'CREATING_LABELS',
  CREATE_BASIC_ITEM = 'CREATE_BASIC_ITEM',
  INBOUND_SHIPPING = 'INBOUND_SHIPPING',

  FORCE_DOC_CHECK = 'FORCE_DOC_CHECK',
  FORCE_PAYMENT_CHECK = 'FORCE_PAYMENT_CHECK',
  REVIEW_DOC_CHECK = 'REVIEW_DOC_CHECK',
  REVIEW_PAYMENT_CHECK = 'REVIEW_PAYMENT_CHECK',
  TOGGLE_DOC_VERIFICATION = 'TOGGLE_DOC_VERIFICATION',
  TOGGLE_PAYMENT_VERIFICATION = 'TOGGLE_PAYMENT_VERIFICATION',
  RUN_SOFT_CREDIT_CHECK = 'RUN_SOFT_CREDIT_CHECK',
  RUN_HARD_CREDIT_CHECK_MAYBE_CARD_ORDER = 'RUN_HARD_CREDIT_CHECK_MAYBE_CARD_ORDER',
}

export const useApplicationModal = ({ application }: UseApplicationModalProps) => {
  const [actionRecord, setActionRecord] = useState<{ action?: AdminActionRecordType; description?: string }>({
    action: undefined,
    description: undefined,
  });

  const [showModal, setShowModal] = useState(false);
  const [action, setAction] = useState<ApplicationActions | null>(null);
  const [docCheckId, setDocCheckId] = useState('');
  const [rejectionReason, setRejectionReason] = useState<string[]>([]);

  const [checkboxes, setCheckboxes] = useState({
    affiliateContributed: false,
    bureauContributed: false,
  });

  const [cancellationType, setCancellationType] = useState<CancellationType>();

  const [approveDenyReason, setApproveDenyReason] = useState('');
  const [docCheckStatus, setDocCheckStatus] = useState<DocCheckStatus>(DocCheckStatus.NotDone);
  const [skipSendEmail, setSkipSendEmail] = useState(false);

  const creditApplcationIdPayload = {
    variables: {
      creditApplicationId: application?.id ?? '',
    },
  };

  const {
    executeAction,
    isLoading: actionLoading,
    errors,
  } = useApplicationActions({
    [ApplicationActions.APPROVE]: useApproveManualReviewMutation({
      variables: {
        creditApplicationId: application?.id ?? '',
        reason: approveDenyReason,
      },
    }),
    [ApplicationActions.DENY_PARTNER_KYC]: useDenyPartnerKycMutation({
      variables: {
        creditApplicationId: application?.id ?? '',
        reason: approveDenyReason,
      },
    }),
    [ApplicationActions.RETRY_PARTNER_KYC]: useRetryPartnerKycMutation({
      variables: {
        creditApplicationId: application?.id ?? '',
      },
    }),
    [ApplicationActions.SEND]: useSendOfferMutation({
      variables: {
        creditApplicationId: application?.id ?? '',
      },
    }),
    [ApplicationActions.CLOSE_APPLICATION]: useCloseApplicationMutation({
      variables: {
        creditApplicationId: application?.id ?? '',
        cancellationType: cancellationType ?? CancellationType.CanceledSoft,
        cancelReasons: rejectionReason,
        affiliateContributed: checkboxes.affiliateContributed,
        bureauContributed: checkboxes.bureauContributed,
        reason: approveDenyReason,
        skipSendEmail: skipSendEmail,
      },
      errorPolicy: 'all',
    }),
    [ApplicationActions.CREATE_LOG]: useCreateActionRecordForCurrentAdminMutation({
      variables: {
        userId: application?.pestoUser?.id ?? '',
        creditApplicationId: application?.id ?? '',
        action: actionRecord?.action ?? AdminActionRecordType.CreditReview,
        description: actionRecord?.description,
      },
    }),
    [ApplicationActions.FORCE_DOC_CHECK]: useAddDocCheckMutation(creditApplcationIdPayload),
    [ApplicationActions.REVIEW_DOC_CHECK]: useReviewDocCheckMutation({
      variables: {
        docCheckId: docCheckId,
        reason: approveDenyReason,
        status: docCheckStatus,
      },
    }),
    [ApplicationActions.TOGGLE_DOC_VERIFICATION]: useToggleDocVerificationMutation({
      variables: {
        creditApplicationId: application?.id ?? '',
        require: !application?.verifyDoc,
      },
    }),
    [ApplicationActions.RUN_SOFT_CREDIT_CHECK]: useRunSoftCreditCheckMutation(creditApplcationIdPayload),
    [ApplicationActions.RUN_HARD_CREDIT_CHECK_MAYBE_CARD_ORDER]:
      useRunHardCreditCheckMaybeCardOrderMutation(creditApplcationIdPayload),
  });

  const handleModalConfirm = useCallback(
    async (refetch: () => Promise<ApolloQueryResult<GetCreditApplicationByIdQuery>>) => {
      if (action) {
        const success = await executeAction(action);

        if (success) {
          setShowModal(false);

          const actionsRequiringRefetch = [
            ApplicationActions.CREATE_LOG,
            ApplicationActions.APPROVE,
            ApplicationActions.DENY_PARTNER_KYC,
            ApplicationActions.RETRY_PARTNER_KYC,
            ApplicationActions.CLOSE_APPLICATION,
            ApplicationActions.SEND,
            ApplicationActions.TOGGLE_DOC_VERIFICATION,
            ApplicationActions.RUN_SOFT_CREDIT_CHECK,
            ApplicationActions.RUN_HARD_CREDIT_CHECK_MAYBE_CARD_ORDER,
          ];

          if (actionsRequiringRefetch.includes(action)) {
            void refetch();
          }
        }
      }
    },
    [action, executeAction, setShowModal],
  );

  const getModalTitle = useCallback(() => {
    if (action === ApplicationActions.CREATING_LABELS) {
      return (
        <div>
          <strong>One moment please. Creating/Retrieving Label.</strong>
        </div>
      );
    }

    return (
      <div>
        Please confirm you want to <strong>{humanize(action || '')}</strong> this{' '}
        {`${action === ApplicationActions.SEND ? ' Offer' : ' Application'}`}
      </div>
    );
  }, [action]);

  const actionRecordOptions = useMemo(() => {
    return Object.values(AdminActionRecordType)?.map((dd) => {
      return { value: dd, label: humanize(dd) };
    });
  }, []);

  const docCheckStatusOptions = useMemo(() => {
    return Object.values(DocCheckStatus)?.map((dd) => {
      return { value: dd, label: humanize(dd) };
    });
  }, []);

  const modalReasonTextArea = (
    <>
      <Label>Reason</Label>
      <TextArea defaultValue={''} onChange={(value) => setApproveDenyReason(value.target.value)} />
    </>
  );

  const modalContent = () => {
    const actionComponents: Partial<Record<ApplicationActions, ReactNode>> = {
      [ApplicationActions.CREATING_LABELS]: <LoadingSpinner size={100} />,
      [ApplicationActions.CLOSE_APPLICATION]: (
        <CloseApplication
          application={application}
          checkboxes={checkboxes}
          setCheckboxes={setCheckboxes}
          rejectionReason={rejectionReason}
          setRejectionReason={setRejectionReason}
          approveDenyReason={approveDenyReason}
          setApproveDenyReason={setApproveDenyReason}
          cancellationType={cancellationType}
          setCancellationType={setCancellationType}
          skipSendEmail={skipSendEmail}
          setSkipSendEmail={setSkipSendEmail}
          closeApplicationError={errors?.[0]}
        />
      ),
      [ApplicationActions.CREATE_LOG]: (
        <div>
          <Label>Record Type</Label>
          <DropdownInput
            options={actionRecordOptions}
            value={actionRecord.action}
            onChange={(value) =>
              setActionRecord({
                ...actionRecord,
                action: actionRecordOptions.find((option) => option.value === value)?.value,
              })
            }
          />
          <Label>Note</Label>
          <TextArea
            defaultValue={''}
            onChange={(value) => setActionRecord({ ...actionRecord, description: value.target.value })}
          />
        </div>
      ),
      [ApplicationActions.APPROVE]: modalReasonTextArea,
      [ApplicationActions.DENY_PARTNER_KYC]: (
        <>
          <Label>Reason (required)</Label>
          <TextArea defaultValue={''} onChange={(value) => setApproveDenyReason(value.target.value)} />
        </>
      ),
      [ApplicationActions.RETRY_PARTNER_KYC]: (
        <>
          <p>This will re-run the Oscilar KYC check using the stored SSN.</p>
          <p className="text-muted-foreground mt-2 text-sm">
            Note: SSN is stored for 7 days. If expired, you must manually approve or deny.
          </p>
        </>
      ),

      [ApplicationActions.REVIEW_DOC_CHECK]: (
        <>
          <Label>Select Doc Check Status</Label>
          <DropdownInput
            options={docCheckStatusOptions}
            value={docCheckStatus}
            onChange={(value) => {
              const status = docCheckStatusOptions.find((option) => option.value === value)?.value;

              if (status) setDocCheckStatus(status);
            }}
          />
          {modalReasonTextArea}
        </>
      ),
      [ApplicationActions.INBOUND_SHIPPING]: modalReasonTextArea,
    };

    return action ? actionComponents[action] : undefined;
  };

  return {
    showModal,
    setShowModal,
    action,
    setAction,
    getModalTitle,
    modalContent,
    docCheckStatus,
    setDocCheckStatus,
    setDocCheckId,

    handleModalConfirm,
    actionLoading,
    errors,
  };
};
